/**
 * Mistify COD "Buy Now"
 * Adds the selected variant to the cart, then goes straight to checkout.
 *
 * - Reads variant + quantity from the button's OWN product form (never the cart drawer).
 * - Uses event delegation, so it keeps working after Dawn re-renders on variant change.
 * - Mirrors Add to cart's enabled/disabled state when the variant changes.
 * - Shows errors in Dawn's existing product-form error box (role="alert").
 */
(() => {
  if (window.mistifyBuyNowInit) return;
  window.mistifyBuyNowInit = true;

  const LABEL_BUSY = 'Processing…';

  const getErrorBox = (form) => {
    const wrapper = form.closest('product-form')?.querySelector('.product-form__error-message-wrapper');
    return { wrapper, text: wrapper?.querySelector('.product-form__error-message') };
  };

  const showError = (form, message) => {
    const { wrapper, text } = getErrorBox(form);
    if (wrapper && text) {
      text.textContent = message;
      wrapper.hidden = false;
    } else {
      console.error('Buy Now:', message);
    }
  };

  const clearError = (form) => {
    const { wrapper } = getErrorBox(form);
    if (wrapper) wrapper.hidden = true;
  };

  const setBusy = (button, busy) => {
    if (busy) {
      button.dataset.label = button.textContent.trim();
      button.textContent = LABEL_BUSY;
    } else if (button.dataset.label) {
      button.textContent = button.dataset.label;
    }
    button.disabled = busy;
    button.setAttribute('aria-busy', String(busy));
  };

  // Keep Buy Now enabled/disabled in step with Add to cart.
  const syncWithAddButton = () => {
    document.querySelectorAll('.buy-now-button').forEach((button) => {
      if (button.getAttribute('aria-busy') === 'true') return;
      const submit = button.form?.querySelector('[name="add"]');
      if (submit) button.disabled = submit.disabled;
    });
  };

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.buy-now-button');
    if (!button || button.disabled) return;

    const form = button.form;
    if (!form) return;
    event.preventDefault();
    clearError(form);

    const idInput = form.querySelector('input[name="id"]');
    const submit = form.querySelector('[name="add"]');
    const variantId = Number(idInput?.value);

    if (!variantId || idInput.disabled) {
      showError(form, window.variantStrings?.unavailable || 'Please select an available option.');
      return;
    }
    if (submit?.disabled) {
      showError(form, window.variantStrings?.soldOut || 'This option is sold out.');
      return;
    }

    // Gift sets: the scent picker must be filled in (snippets/mistify-scent-picker.liquid).
    if (typeof window.mstPickMissing === 'function' && window.mstPickMissing()) return;

    // form.elements also finds inputs linked with form="..." (Dawn's quantity selector, scent picker).
    const qtyField = form.elements.namedItem('quantity');
    const quantity = Math.max(1, parseInt(qtyField?.value, 10) || 1);
    const properties = {};
    Array.from(form.elements).forEach((el) => {
      const m = el.name && el.name.match(/^properties\[(.+)\]$/);
      if (m && el.value) properties[m[1]] = el.value;
    });

    setBusy(button, true);

    try {
      const response = await fetch(`${window.routes?.cart_add_url || '/cart/add'}.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variantId, quantity, properties }] }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.description || data.message || window.cartStrings?.error || 'Could not add to cart.');
      }

      window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
    } catch (error) {
      showError(form, error.message);
      setBusy(button, false);
    }
  });

  // Dawn publishes variantChange after it updates Add to cart.
  // constants.js declares PUB_SUB_EVENTS with `const`, so it is not on window.
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.variantChange, syncWithAddButton);
  }

  // Reset the button if the shopper comes back from checkout with the Back button.
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;
    document.querySelectorAll('.buy-now-button[aria-busy="true"]').forEach((button) => setBusy(button, false));
    syncWithAddButton();
  });
})();